import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { useFavorites } from "../lib/useFavorites";
import { useNation, setNation } from "../lib/useNation";
import { Page, Card, RichLoader, ErrorBox, SectionHeader } from "../components/ui";
import { useDelayedFlag } from "../lib/useDelayedFlag";
import { useWaitMessage } from "../lib/useWaitMessage";
import { ElectionCard } from "../components/ElectionCard";
import { WorldCard } from "../components/WorldCard";
import { ActivityFeed } from "../components/ActivityFeed";
import { PollWidget } from "../components/PollWidget";
import { ClaimBadge } from "../components/StatusBadge";
import { flag } from "../lib/flag";
import type { Candidacy, CountryLite, ElectionSummary, RaceCandidacies } from "@shared/types";
import { candidatePath } from "@shared/share-links";
import { useT } from "../i18n";

interface FavElection {
  id: string;
  countryId: string;
  countryName: string;
  countryCode: string;
  type: string;
}

export default function Home() {
  const { nation } = useNation();
  return <Page>{nation ? <NationHub nation={nation} /> : <Onboarding />}</Page>;
}

/** First visit: no nation picked. Big display heading + a nation tile grid. */
function Onboarding() {
  const t = useT();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const { data, loading, error } = useAsync(() => api.countries(), []);
  const showRich = useDelayedFlag(loading, 400);
  const waitKey = useWaitMessage(loading);
  const filtered = (data ?? []).filter((c) => `${c.name} ${c.code}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="max-w-[900px] mx-auto pt-[4vh]">
      <div className="kicker">{t("home.kicker")}</div>
      <h1 className="h-xl">{t("home.pickSide")}</h1>
      <p className="text-muted mt-4 mb-6 max-w-xl">{t("home.onboardBlurb")}</p>

      <input
        className="input-x max-w-md mb-6"
        placeholder={t("search.countryPlaceholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {showRich && <RichLoader headline={t("common.loadingCountries")} message={t(waitKey)} />}
      {error && <ErrorBox msg={error} />}

      <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))" }}>
        {filtered.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              setNation(c);
              nav("/");
            }}
            className="card-x card-hov text-center"
          >
            <div className="text-[40px]">{flag(c.code)}</div>
            <div className="font-disp font-bold mt-2 truncate">{c.name}</div>
          </button>
        ))}
      </div>
      {!loading && filtered.length === 0 && <p className="text-muted text-sm mt-4">{t("search.noMatches")}</p>}
    </div>
  );
}

/** The picked nation's home: header + stat strip, President + Congress races, a
 * spotlight, the activity wire, nation polls, and a peek at the world. */
function NationHub({ nation }: { nation: CountryLite }) {
  const t = useT();
  const nav = useNavigate();
  const favE = useFavorites<FavElection>("elections");
  const elections = useAsync(() => api.countryElections(nation.id), [nation.id]);
  const world = useAsync(() => api.world(), []);
  const activity = useAsync(() => api.activity(nation.id), [nation.id]);
  const polls = useAsync(() => api.polls({ country: nation.id }), [nation.id]);
  const races = useAsync(
    () =>
      Promise.all([
        api.raceCandidacies(nation.id, "president").catch(() => null),
        api.raceCandidacies(nation.id, "congress").catch(() => null),
      ]),
    [nation.id],
  );

  const showRichElections = useDelayedFlag(elections.loading, 400);
  const showRichWorld = useDelayedFlag(world.loading, 400);
  const waitElections = useWaitMessage(elections.loading);
  const waitWorld = useWaitMessage(world.loading);

  const list = elections.data ?? [];
  const pres = list.find((e) => e.type === "president");
  const cong = list.find((e) => e.type === "congress");
  const current = [pres, cong].filter(Boolean) as ElectionSummary[];
  const liveCount = current.filter((e) => e.phase === "live").length;
  const activeVoters = current[0]?.activePopulation ?? 0;
  const candidates = races.data ?? [];
  // People running across the shown races: in-game candidates once the record
  // exists, else WarUrna pre-declarations — computed server-side and deduped.
  const candCount = current.reduce((s, e) => s + e.candidateCount, 0);
  const spotlight = pickSpotlight(candidates);

  const openRace = (e: ElectionSummary) => nav(`/c/${nation.id}/race/${e.type}`, { state: { phase: e.phase } });

  return (
    <section>
      {/* nation header */}
      <div className="flex items-center gap-5 flex-wrap">
        <div className="big-crest">{flag(nation.code)}</div>
        <div className="min-w-0">
          <div className="kicker">{t("home.yourNation")}</div>
          <h1 className="h-xl leading-none">{nation.name.toUpperCase()}</h1>
          <div className="stat-strip">
            <div><div className="n">{liveCount}</div><div className="l">{t(liveCount === 1 ? "home.raceLive" : "home.racesLive")}</div></div>
            <div><div className="n">{activeVoters.toLocaleString()}</div><div className="l">{t("home.activeVoters")}</div></div>
            <div><div className="n">{candCount}</div><div className="l">{t(candCount === 1 ? "home.candidateRunning" : "home.candidatesRunning")}</div></div>
          </div>
        </div>
        <Link to="/candidates" className="btn ml-auto self-start no-underline">{t("home.declareCandidacy")}</Link>
      </div>

      {/* races */}
      <div className="sec mt-11">
        <SectionHeader title={t("home.yourRaces")} />
        {showRichElections && <RichLoader headline={t("home.loadingRaces")} message={t(waitElections)} />}
        {elections.error && <ErrorBox msg={elections.error} />}
        <div className="grid gap-4 sm:grid-cols-2">
          {current.map((e) => (
            <ElectionCard
              key={e.id || `sched-${e.type}`}
              e={e}
              fav={e.scheduled ? false : favE.isFav(e.id)}
              onFav={() => {
                if (!e.scheduled) favE.toggle({ id: e.id, countryId: nation.id, countryName: e.countryName, countryCode: e.countryCode, type: e.type });
              }}
              onOpen={() => {
                if (!e.scheduled) nav(`/c/${nation.id}/e/${e.id}`);
              }}
              onOpenRace={() => openRace(e)}
            />
          ))}
        </div>
      </div>

      {/* spotlight */}
      {spotlight && (
        <div className="sec mt-11">
          <SectionHeader title={t("home.fromTrail")} moreTo="/candidates" moreLabel={`${t("home.allCandidates")} →`} />
          <Link to={candidatePath(spotlight.username)} className="no-underline">
            <Card className="card-hov">
              <div className="flex items-center justify-between gap-2">
                <span className="font-disp font-bold text-lg truncate">{spotlight.username}</span>
                <ClaimBadge claimStatus={spotlight.claimStatus} />
              </div>
              <div className="eyebrow mt-1 normal-case">{spotlight.party ?? t("common.independent")} · {spotlight.office === "president" ? t("office.presidentTitle") : t("office.congressTitle")}</div>
              {spotlight.intro && <p className="text-sm text-muted mt-2 line-clamp-2">{spotlight.intro}</p>}
              <div className="eyebrow mt-3 normal-case">
                {t("home.measuresCount", { n: spotlight.measures.length })} · {t("home.readPlatform")} →
              </div>
            </Card>
          </Link>
        </div>
      )}

      {/* activity */}
      <div className="sec mt-11">
        <SectionHeader title={t("home.latestIn", { nation: nation.name })} />
        <ActivityFeed events={activity.data} loading={activity.loading} error={activity.error} />
      </div>

      {/* nation polls */}
      {(polls.data?.length ?? 0) > 0 && (
        <div className="sec mt-11">
          <SectionHeader title={t("home.nationPolls", { nation: nation.name })} moreTo="/polls" moreLabel={`${t("home.allPolls")} →`} />
          <div className="grid gap-4 sm:grid-cols-2">
            {polls.data!.slice(0, 2).map((p) => (
              <PollWidget key={p.id} poll={p} linkToDetail />
            ))}
          </div>
        </div>
      )}

      {/* world */}
      <div className="sec mt-11">
        <SectionHeader title={t("home.aroundWorld")} moreTo="/world" moreLabel={`${t("home.fullFeed")} →`} />
        {showRichWorld && <RichLoader headline={t("home.aroundWorld")} message={t(waitWorld)} />}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(world.data ?? []).slice(0, 6).map((e) => (
            <WorldCard key={e.id} e={e} onOpen={() => nav(`/c/${e.countryId}/e/${e.id}`)} />
          ))}
        </div>
      </div>
    </section>
  );
}

/** Featured candidacy: prefer a verified, published one with a pitch. */
function pickSpotlight(races: (RaceCandidacies | null)[]): Candidacy | null {
  const all = races.flatMap((r) => r?.candidacies ?? []).filter((c) => c.review === "published");
  if (all.length === 0) return null;
  const score = (c: Candidacy) => (c.claimStatus === "claimed" ? 2 : 0) + (c.intro ? 1 : 0);
  return [...all].sort((a, b) => score(b) - score(a))[0];
}
